import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { Input } from "@/v2/components/ui/input";
import {
	type FieldOptionList,
	getValueLabel,
	normalizeOptions,
	type OptionItem,
} from "./fields";
import type { EnumFilterField } from "./types";
import { valueDecoration } from "./valueDecoration";

export interface ValueChecklistProps {
	field: EnumFilterField;
	options: FieldOptionList;
	/** null means implicit all selected. */
	selectedValues: ReadonlyArray<string> | null;
	singleSelect?: boolean;
	onToggle: (value: string) => void;
	onSelectAll: () => void;
	onSelectNone: () => void;
}

const ROW_HEIGHT = 32;
const ROW_HEIGHT_WITH_SUBLABEL = 48;
const SCROLL_MAX_HEIGHT = 288;

export function ValueChecklist({
	field,
	options,
	selectedValues,
	singleSelect = false,
	onToggle,
	onSelectAll,
	onSelectNone,
}: ValueChecklistProps) {
	const [query, setQuery] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);
	// biome-ignore lint/correctness/useExhaustiveDependencies: field switch reset only.
	useEffect(() => {
		setQuery("");
	}, [field]);

	const normalized = useMemo<ReadonlyArray<OptionItem>>(
		() => normalizeOptions(options),
		[options],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return normalized;
		return normalized.filter((o) => {
			const label = getValueLabel(field, o.label).toLowerCase();
			if (label.includes(q)) return true;
			if (o.sublabel?.toLowerCase().includes(q)) return true;
			return false;
		});
	}, [normalized, query, field]);

	const selectedSet = selectedValues === null ? null : new Set(selectedValues);
	const allOptionValues = normalized.map((option) => option.value);
	const allSelected =
		selectedSet === null ||
		(allOptionValues.length > 0 &&
			allOptionValues.every((value) => selectedSet.has(value)));
	const noneSelected = selectedSet !== null && selectedSet.size === 0;
	const selectedCount =
		selectedSet === null ? allOptionValues.length : selectedSet.size;
	const bulkChecked = allSelected
		? true
		: noneSelected
			? false
			: "indeterminate";
	const bulkLabel = allSelected
		? "All selected"
		: noneSelected
			? "None selected"
			: `${selectedCount} selected`;
	const toggleAll = () => {
		if (allSelected) onSelectNone();
		else onSelectAll();
	};

	const virtualizer = useVirtualizer({
		count: filtered.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: (i) =>
			filtered[i]?.sublabel ? ROW_HEIGHT_WITH_SUBLABEL : ROW_HEIGHT,
		overscan: 6,
	});

	const totalSize = virtualizer.getTotalSize();
	const items = virtualizer.getVirtualItems();

	return (
		<div className="flex flex-col">
			{!singleSelect && (
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleAll}
						aria-label={allSelected ? "Deselect all" : "Select all"}
						title={bulkLabel}
						className="size-8 shrink-0 rounded-sm"
					>
						<Checkbox
							checked={bulkChecked}
							tabIndex={-1}
							aria-hidden
							className="pointer-events-none shrink-0"
						/>
					</Button>
					<Input
						ref={inputRef}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Filter…"
						className="h-8 min-w-0 flex-1 px-2 text-xs"
					/>
				</div>
			)}
			{singleSelect && (
				<Input
					ref={inputRef}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Filter…"
					className="h-8 px-2 text-xs"
				/>
			)}
			<div
				ref={scrollRef}
				className="mt-2 overflow-y-auto"
				style={{ maxHeight: SCROLL_MAX_HEIGHT }}
			>
				{filtered.length === 0 ? (
					<div className="px-2 py-3 text-center text-xs text-muted-foreground">
						No values
					</div>
				) : (
					<div
						style={{ height: totalSize, position: "relative", width: "100%" }}
					>
						{items.map((row) => {
							const opt = filtered[row.index];
							const checked =
								selectedSet === null || selectedSet.has(opt.value);
							const hasSublabel = !!opt.sublabel;
							return (
								<div
									key={opt.value}
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										width: "100%",
										height: row.size,
										transform: `translateY(${row.start}px)`,
									}}
								>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => onToggle(opt.value)}
										className="h-full w-full justify-start gap-2 rounded-sm px-2 py-1 text-xs font-normal hover:bg-accent hover:text-accent-foreground"
									>
										<Checkbox
											checked={checked}
											tabIndex={-1}
											aria-hidden
											className="pointer-events-none shrink-0"
										/>
										{valueDecoration(field, opt.value)}
										<div className="flex min-w-0 flex-1 flex-col items-start">
											<span className="w-full truncate text-left">
												{getValueLabel(field, opt.label)}
											</span>
											{hasSublabel && (
												<span className="w-full truncate text-left text-[11px] text-muted-foreground">
													{opt.sublabel}
												</span>
											)}
										</div>
									</Button>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
